import { useEffect, useState } from "react";
import { api } from "../lib/api";
import ClientLogoBlock from "./ClientLogoBlock";

const emptyNew = {
  client_id: "",
  client_name: "",
  client_contact: "",
  client_clr: "",
  client_ena: 1,
};

const HEX6 = /^[0-9A-Fa-f]{6}$/;

/* -- compact color picker (picker + hex text + live swatch) -- */
function ColorPicker({ value, onChange }) {
  const valid = HEX6.test(value || "");
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="color"
        data-testid="cm-clr-picker"
        value={valid ? `#${value}` : "#000080"}
        onChange={(e) => onChange(e.target.value.replace(/^#/, "").toLowerCase())}
        style={{ width: 44, height: 32, padding: 0, border: "1px solid var(--nyse-line)", borderRadius: 4, cursor: "pointer" }}
        title="Pick a color"
      />
      <input
        data-testid="cm-clr"
        value={value}
        maxLength={7}
        placeholder="000080"
        onChange={(e) => onChange(e.target.value.replace(/^#/, ""))}
        style={{ flex: 1 }}
      />
      <span
        className="cm-swatch"
        data-testid="cm-clr-swatch"
        style={{
          background: valid ? `#${value}` : "transparent",
          border: "1px solid var(--nyse-line)",
        }}
      />
    </div>
  );
}

/* -- the form body inside the modal -- */
function ClientForm({ form, isNew, set, client }) {
  return (
    <div className="form-grid">
      <div className="form-field">
        <label>Client ID <span className="req">*</span> (≤ 6)</label>
        <input
          data-testid="cm-id"
          value={form.client_id}
          maxLength={6}
          disabled={!isNew}
          onChange={(e) => set("client_id", e.target.value.toUpperCase())}
          required={isNew}
        />
      </div>
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>Client name <span className="req">*</span></label>
        <input
          data-testid="cm-name"
          value={form.client_name}
          maxLength={50}
          onChange={(e) => set("client_name", e.target.value)}
          required
        />
      </div>
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>Contact (≤ 50)</label>
        <input
          data-testid="cm-contact"
          value={form.client_contact}
          maxLength={50}
          placeholder="Name + phone or email"
          onChange={(e) => set("client_contact", e.target.value)}
        />
      </div>
      <div className="form-field">
        <label>Theme color</label>
        <ColorPicker
          value={form.client_clr}
          onChange={(v) => set("client_clr", v)}
        />
      </div>
      <div className="form-field">
        <label>Status</label>
        <select
          data-testid="cm-ena"
          value={form.client_ena}
          onChange={(e) => set("client_ena", Number(e.target.value))}
        >
          <option value={1}>Enabled (1)</option>
          <option value={0}>Disabled (0)</option>
        </select>
      </div>
      {!isNew && <ClientLogoBlock client={client} />}
    </div>
  );
}

/* -- diff form against original row to build a patch payload -- */
function buildPatch(form, client) {
  const patch = {};
  if (form.client_name !== client.client_name) patch.client_name = form.client_name;
  if (form.client_contact !== (client.client_contact || "")) patch.client_contact = form.client_contact;
  if ((form.client_clr || "") !== (client.client_clr || "")) patch.client_clr = form.client_clr || null;
  if (Number(form.client_ena) !== Number(client.client_ena)) patch.client_ena = Number(form.client_ena);
  return patch;
}

/* -- validate + shape the create payload (throws on bad color) -- */
function buildCreatePayload(form) {
  const clrClean = (form.client_clr || "").trim().replace(/^#/, "");
  if (clrClean && !HEX6.test(clrClean)) {
    throw new Error("Color must be 6-char hex (e.g. 000080)");
  }
  return {
    ...form,
    client_id: form.client_id.trim().toUpperCase(),
    client_clr: clrClean || null,
    client_ena: Number(form.client_ena),
  };
}

function ClientModal({ mode, client, canWrite = true, onClose, onSaved }) {
  const isNew = mode === "new";
  const [form, setForm] = useState(
    isNew ? { ...emptyNew } : {
      client_id: client.client_id,
      client_name: client.client_name || "",
      client_contact: client.client_contact || "",
      client_clr: client.client_clr || "",
      client_ena: client.client_ena ?? 1,
    }
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (isNew) {
        await api.createClient(buildCreatePayload(form));
      } else {
        const patch = buildPatch(form, client);
        if (Object.keys(patch).length) {
          await api.updateClient(client.client_id, patch);
        }
      }
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete client "${client.client_id}"? This cannot be undone.`)) return;
    setErr("");
    setBusy(true);
    try { await api.deleteClient(client.client_id); onSaved(); }
    catch (e2) { setErr(e2.response?.data?.detail || e2.message); }
    finally { setBusy(false); }
  };

  let submitLabel = "Save changes";
  if (busy) submitLabel = "Saving…";
  else if (isNew) submitLabel = "Create client";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }} data-testid="client-modal">
        <div className="modal-head">
          <h2>{isNew ? "New Client" : `Edit client · ${client.client_id}`}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="cm-close">×</button>
        </div>
        <form className="modal-form" onSubmit={submit}>
          <ClientForm form={form} isNew={isNew} set={set} client={client} />
          {err && <div className="form-error" data-testid="cm-error">{err}</div>}
          <div className="modal-actions no-divider" style={{ justifyContent: "space-between" }}>
            <div>
              {!isNew && canWrite && (
                <button
                  type="button"
                  className="btn-danger"
                  data-testid="cm-delete"
                  onClick={onDelete}
                  disabled={busy}
                >Delete client</button>
              )}
            </div>
            {canWrite && (
              <button
                type="submit"
                className="btn-primary"
                data-testid="cm-submit"
                disabled={busy}
                style={{ width: "auto", padding: "10px 22px", margin: 0 }}
              >
                {submitLabel}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/* -- row in the clients table -- */
function ClientRow({ c, onClick }) {
  const enabled = Number(c.client_ena) === 1;
  return (
    <tr
      key={c.client_id}
      onClick={onClick}
      data-testid={`client-row-${c.client_id}`}
      style={{ cursor: "pointer" }}
    >
      <td>
        {c.client_logo_url ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL || ""}${c.client_logo_url}`}
            alt={c.client_id}
            style={{ height: 28, width: "auto", maxWidth: 90, objectFit: "contain" }}
          />
        ) : <span className="muted">—</span>}
      </td>
      <td><b>{c.client_id}</b></td>
      <td>{c.client_name}</td>
      <td className="muted">{c.client_contact || "—"}</td>
      <td>
        {c.client_clr ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span className="cm-swatch" style={{ background: `#${c.client_clr}`, border: "1px solid var(--nyse-line)" }} />
            <span className="muted">#{c.client_clr}</span>
          </span>
        ) : <span className="muted">—</span>}
      </td>
      <td>
        {enabled
          ? <span className="pay-lit-approved">ENABLED</span>
          : <span className="status-pill" style={{ background: "#e5e7eb", border: "1px solid #9ca3af", color: "#374151" }}>DISABLED</span>}
      </td>
    </tr>
  );
}

/* -- header bar: title + collapse toggle + "+ New client" -- */
function ClientsSectionHeader({ standalone, isOpen, onToggle, canWrite, onNew }) {
  const onHeaderClick = standalone ? undefined : onToggle;
  const onHeaderKey = standalone
    ? undefined
    : (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle && onToggle(); } };
  return (
    <div
      className="admin-section-header"
      role={standalone ? undefined : "button"}
      tabIndex={standalone ? undefined : 0}
      data-testid="admin-toggle-clients"
      onClick={onHeaderClick}
      onKeyDown={onHeaderKey}
      aria-expanded={standalone ? undefined : isOpen}
    >
      {!standalone && <span className="admin-caret">▾</span>}
      <p className="section-title" style={{ margin: 0 }}>Clients</p>
      {canWrite && (
        <span
          className="btn-navy admin-new-btn"
          data-testid="new-client-btn"
          role="button"
          tabIndex={isOpen ? 0 : -1}
          onClick={(e) => { e.stopPropagation(); if (isOpen) onNew(); }}
          onKeyDown={(e) => { if (isOpen && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); e.stopPropagation(); onNew(); } }}
          style={{ visibility: isOpen ? "visible" : "hidden", pointerEvents: isOpen ? "auto" : "none" }}
        >+ New client</span>
      )}
    </div>
  );
}

/* -- table body: handles loading / data states -- */
function ClientsTable({ clients, onRowEdit }) {
  if (!clients) {
    return <div className="loading-row"><span className="spinner" /> Loading clients…</div>;
  }
  return (
    <div className="list-scroll">
      <table className="list-table list-table-compact txn-table">
        <thead>
          <tr className="list-head-labels">
            <th>Logo</th>
            <th>Client ID</th>
            <th>Name</th>
            <th>Contact</th>
            <th>Color</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((c) => (
            <ClientRow
              key={c.client_id}
              c={c}
              onClick={() => onRowEdit(c)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminClients({ open = false, onToggle, standalone = false, canWrite = true }) {
  const [clients, setClients] = useState(null);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);
  const isOpen = standalone || open;

  const load = async () => {
    try { setClients(await api.listClients()); }
    catch (e) { setErr(e.message); }
  };
  useEffect(() => { if (isOpen) load(); }, [isOpen]);

  return (
    <>
      <section className={`admin-section ${isOpen ? "open" : "closed"} ${standalone ? "standalone" : ""}`} data-testid="admin-section-clients">
        <ClientsSectionHeader
          standalone={standalone}
          isOpen={isOpen}
          onToggle={onToggle}
          canWrite={canWrite}
          onNew={() => setModal({ mode: "new" })}
        />
        <div className="admin-section-body">
          <div className="card list-card" data-testid="admin-clients-card">
            {err && <div className="form-error" style={{ margin: "14px 18px 0" }}>{err}</div>}
            <ClientsTable
              clients={clients}
              onRowEdit={(c) => setModal({ mode: "edit", client: c })}
            />
          </div>
        </div>
      </section>

      {modal && (
        <ClientModal
          mode={modal.mode}
          client={modal.client}
          canWrite={canWrite}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}
